import { Box, Button, CircularProgress, useTheme } from '@mui/material';
import { Trash2 } from 'lucide-react';
import React, { PropsWithChildren, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import Dialog from 'components/dialog';
import { useAccountState } from 'contexts/account/account-context';
import { useCreateScenario } from 'views/bluviu-nutrition/features/scenarios/hooks/use-create-scenario';
import { ScenariosParams } from 'views/bluviu-nutrition/features/scenarios/types/scenarios-params.type';
import { ScenarioListItem } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-list-item.type';

type Props = PropsWithChildren & {
  button?: React.ReactElement;
  scenario: ScenarioListItem;
};

const ScenarioDeleteDialog: React.FC<Props> = ({ button, scenario }) => {
  const queryClient = useQueryClient();
  const params = useParams<ScenariosParams>();
  const theme = useTheme();
  const { deleteScenario } = useCreateScenario();
  const {
    account: { company },
  } = useAccountState();
  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);

  // handleDeleteScenario is using the old delete scenario mutation. It will be replaced when the new one is ready.
  const handleDeleteScenario = () => {
    deleteScenario.mutate(
      { globalCompanyId: company!.companyId, scenarioId: scenario.id },
      {
        onSuccess: (data) => {
          queryClient.invalidateQueries({
            queryKey: ['getR2Scenarios', params.globalSiteId]
          });
          dialogRef.current?.setOpen(false);
        },
      }
    );
  };

  return (
    <Dialog
      ref={dialogRef}
      maxWidth={'xs'}
      title={`Delete scenario "${scenario.name}"?`}
      button={
        button || (
          <Button
            variant="text"
            startIcon={<Trash2 size={20} />}
            sx={{
              padding: 0,
              color: theme.palette.darkBlue.main,
              textTransform: 'none',
              justifyContent: 'flex-start',
            }}>
            Delete scenario
          </Button>
        )
      }>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          <Box>
            <FormattedMessage id="general.deleteComment" />
          </Box>
          <Box display={'flex'} justifyContent={'end'} gap={4}>
            <Button variant="outlined" type="submit" size="small" onClick={() => dialogRef.current?.close()}>
              <FormattedMessage id="general.cancel" />
            </Button>
            <Button
              onClick={() => handleDeleteScenario()}
              disabled={deleteScenario.isPending}
              startIcon={deleteScenario.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
              <FormattedMessage id="general.yesDelete" />
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default React.memo(ScenarioDeleteDialog);
